import usePageTitle from '../../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { products as productsApi, admin, getImageUrl } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Clock, 
  Search, 
  RefreshCw, 
  Check, 
  X,
  Package,
  Filter,
  Loader2
} from 'lucide-react';

const AdminOutOfStock = () => {
  usePageTitle('Backorder Management');
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [outOfStockProducts, setOutOfStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'out-of-stock'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const productsRes = await productsApi.getAll({ limit: 500 });
      const allProds = productsRes.data.products || [];
      
      setAllProducts(allProds);
      setOutOfStockProducts(allProds.filter(p => p.is_out_of_stock));
      
      // Extract unique categories
      const uniqueCategories = [...new Set(allProds.map(p => p.category).filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // Filter products based on view mode, search, and category
  const filteredProducts = (viewMode === 'out-of-stock' ? outOfStockProducts : allProducts)
    .filter(product => {
      const matchesSearch = !searchTerm || 
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });

  const handleToggleOutOfStock = async (productId, currentStatus) => {
    try {
      await admin.toggleOutOfStock(productId, !currentStatus);
      toast.success(currentStatus ? 'Product marked as in stock' : 'Product marked as backorder');
      loadData();
    } catch (error) {
      toast.error('Failed to update product status');
    }
  };

  const handleBulkOutOfStock = async (isOutOfStock) => {
    if (selectedProducts.length === 0) {
      toast.error('No products selected');
      return;
    }
    
    try {
      await admin.bulkToggleOutOfStock(selectedProducts, isOutOfStock);
      toast.success(`${selectedProducts.length} products marked as ${isOutOfStock ? 'out of stock' : 'in stock'}`);
      setSelectedProducts([]);
      loadData();
    } catch (error) {
      toast.error('Failed to update products');
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.id));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Out of Stock Management</h1>
          <p className="text-gray-500 text-sm">Mark products for backorder - customers can still order, items ship when available</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Products</p>
                <p className="text-3xl font-bold text-gray-900">{allProducts.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">On Backorder</p>
                <p className="text-3xl font-bold text-orange-600">{outOfStockProducts.length}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">In Stock</p>
                <p className="text-3xl font-bold text-green-600">{allProducts.length - outOfStockProducts.length}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Check className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-4 items-center">
              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                    viewMode === 'all' 
                      ? 'bg-white shadow text-gray-900' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setViewMode('all')}
                >
                  All Products
                </button>
                <button
                  className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                    viewMode === 'out-of-stock' 
                      ? 'bg-white shadow text-gray-900' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setViewMode('out-of-stock')}
                >
                  Backorder Only
                </button>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>

              {/* Category Filter */}
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bulk Actions */}
            {selectedProducts.length > 0 && (
              <div className="flex gap-2 items-center">
                <span className="text-sm text-gray-500">{selectedProducts.length} selected</span>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-orange-300 text-orange-600 hover:bg-orange-50"
                  onClick={() => handleBulkOutOfStock(true)}
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Mark Backorder
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleBulkOutOfStock(false)}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Mark In Stock
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="p-3 text-left">
                    <Checkbox
                      checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  </th>
                  <th className="p-3 text-left">Product</th>
                  <th className="p-3 text-left">SKU</th>
                  <th className="p-3 text-left">Category</th>
                  <th className="p-3 text-left">Price</th>
                  <th className="p-3 text-center">Status</th>
                  <th className="p-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-gray-500">
                      <PackageX className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No products found</p>
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map(product => (
                    <tr key={product.id} className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <Checkbox
                          checked={selectedProducts.includes(product.id)}
                          onCheckedChange={() => toggleProductSelection(product.id)}
                        />
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          {product.images?.[0] ? (
                            <img
                              src={getImageUrl(product.images[0])}
                              alt={product.name}
                              className="w-12 h-12 object-cover rounded"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <Link 
                              to={`/admin/products/${product.id}`}
                              className="font-medium text-gray-900 hover:text-blue-600"
                            >
                              {product.name}
                            </Link>
                            {product.is_on_sale && (
                              <Badge variant="destructive" className="ml-2 text-xs">Sale</Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-gray-600">{product.sku}</td>
                      <td className="p-3 text-gray-600 capitalize">{product.category}</td>
                      <td className="p-3 text-gray-900 font-medium">${product.price?.toFixed(2)}</td>
                      <td className="p-3 text-center">
                        {product.is_out_of_stock ? (
                          <Badge className="bg-orange-100 text-orange-700">Backorder</Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-green-100 text-green-700">In Stock</Badge>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          className={product.is_out_of_stock ? "" : "border-orange-300 text-orange-600 hover:bg-orange-50"}
                          onClick={() => handleToggleOutOfStock(product.id, product.is_out_of_stock)}
                        >
                          {product.is_out_of_stock ? (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              Mark In Stock
                            </>
                          ) : (
                            <>
                              <Clock className="w-4 h-4 mr-1" />
                              Backorder
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminOutOfStock;
